import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { withTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from '@mui/material/Grid';
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { isEmpty, isEqual, isNull } from "lodash"
import { useDispatch } from "react-redux";

import rpDistributionNetworkService from "@plannel/services/rp/rp-distribution-network-service"
import locationService from "@plannel/services/data/location-service";
import BodService from "@plannel/services/data/bod-service";
import BodItemService from "@plannel/services/data/bod-item-service";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import DataState from "@plannel/components/aggrid/DataState"
import { AddButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import AppCard from "@plannel/components/ui/AppCard";
import SegmentedControl from "@plannel/components/ui/SegmentedControl";
import StatPill from "@plannel/components/ui/StatPill";
import { getNodeNavActions, DIST_KIND } from "@plannel/components/flow/distNetworkNavActions";
import DistributionNetworkGraph from "@plannel/components/distribution-network/DistributionNetworkGraph";
import DistributionNetworkLocationList from "@plannel/components/distribution-network/DistributionNetworkLocationList";
import DistributionNetworkInspector from "@plannel/components/distribution-network/DistributionNetworkInspector";
import {
  buildNetwork, layoutGraph, linksForBods, reachTones, REACH_ARROW, REACH_DIR, DEFAULT_DEPTH, DEPTH_ALL,
} from "@plannel/components/distribution-network/distNetworkModel";
import DateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import { FS } from "@plannel/utils/font-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import siteService from "@plannel/services/data/site-master-service";
import { RPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";

/*
  RP > Distribution Network.

  뷰 두 개가 같은 네트워크를 다른 질문에 맞춰 보여준다.
    Graph — 어떻게 연결돼 있나 (계층 lane + 사이트 밴드, 목록·인스펙터와 3분할)
    Table — 고치기 (기존 AG Grid CRUD 4종 그대로)

  선택(selectedLocation)은 두 뷰가 공유한다 — 그래프에서 고른 위치가 Table 로 넘어가도 그대로
  선택돼 있어야 "보다가 고친다"가 한 흐름으로 이어진다.

  ※ 예전의 전체화면 토글은 없앴다. 그래프 패널이 화면 1/4 이라 필요했던 장치인데,
    지금은 Graph 뷰 자체가 페이지 전체를 쓴다.
*/
const LOCATION_GRID = "LOCATION_GRID";
const BOD_GRID = "BOD_GRID";
const SUPPLIED_BOD_ITEM_GRID = "SUPPLIED_BOD_ITEM_GRID";
const SUPPLYING_BOD_ITEM_GRID = "SUPPLYING_BOD_ITEM_GRID";

const VIEW = { GRAPH: "graph", TABLE: "table" };

/* 따라가기 검색은 품목 코드로만 찾는다. 검색 인덱스는 맞은 필드 하나만 돌려주므로
   이름으로 맞으면 코드를 알 수 없고, 따라가기(item-bods)는 코드로만 조회한다. */
const FOLLOW_SEARCH_FIELDS = ["itemCd"];

// Autocomplete 는 고른 항목을 { itemCd, groupKey } 로, 엔터·검색 버튼은 입력 문자열로 넘긴다.
const resolveItemCd = selected => {
  if (!selected) {
    return "";
  }
  if (typeof selected === "string") {
    return selected.trim();
  }
  const code = selected.itemCd ?? selected[selected.groupKey];
  return code ? String(code).trim() : "";
};

const { locationType } = CodeUtil;
const { snackBarMessageColor } = ColorUtil;

const RpDistributionNetwork = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const theme = useTheme();
  const { isPermitted } = useMenuAccess();

  const [loading, setLoading] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const nodeGridRef = useRef();
  const bodGridRef = useRef();
  const suppliedProdGridRef = useRef();
  const supplyingProdGridRef = useRef();

  // 저장된 뷰가 지금 없는 값이면 Graph 로 떨어뜨린다(세그먼트가 아무것도 선택 안 된 상태로 열리지 않게).
  const [type, setType] = useState(isEqual(reduxViewState?.type, VIEW.TABLE) ? VIEW.TABLE : VIEW.GRAPH);
  const [locations, setLocations] = useState([]);
  const [originLocations, setOriginLocations] = useState([]);
  const [destLocations, setDestLocations] = useState([]);

  const [activeBods, setActiveBods] = useState([]);
  const [billOfDistribution, setBillOfDistribution] = useState([]);
  const [bodItemCounts, setBodItemCounts] = useState({});
  const [suppliedItems, setSuppliedItems] = useState([]);
  const [supplyingItems, setSupplyingItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteMap, setSiteMap] = useState({});
  const [selectedLocation, setSelectedLocation] = useState(reduxViewState?.selectedLocation ?? null);
  const [deleteTableLocation, setDeleteTableLocation] = useState([]);
  const [deleteTableBod, setDeleteTableBod] = useState([]);

  const [bodGridApi, setBodGridApi] = useState(null);
  // 그래프 노드 우클릭 컨텍스트 메뉴 { x, y, kind, code, name, nodeType } | null
  const [ctxMenu, setCtxMenu] = useState(null);

  /*
    품목 축 — 거르지 않고 짚어 들어간다.

    품목을 먼저 고르는 대신, 그래프에서 선을 눌러 "이 구간으로 무엇이 가나"를 보고, 그중 하나를
    「따라가기」로 짚으면 그 품목의 경로가 전체 네트워크 위에 칠해진다. 품목명을 미리 알 필요가 없고,
    거르지 않으므로 그 경로가 큰 그림의 어디쯤인지도 함께 보인다.

      selectedLink — 지금 고른 링크(그래프의 link 객체)
      probeItems   — 지금 설명 중인 대상(링크 또는 거점)을 타는 품목
      flowItemCd   — 따라가는 품목
      flowSourcing — 그 품목이 흐르는 BOD id → { originPriority, originAllocRatio }
                     키로는 칠할 선을, 값으로는 선에 달 우선순위·비율 라벨을 정한다
  */
  const [selectedLink, setSelectedLink] = useState(null);
  // 허브에서 합쳐진 줄기를 눌렀을 때 — 어느 갈래인지부터 고르게 한다.
  const [selectedBundle, setSelectedBundle] = useState(null);
  /* 품목 목록은 링크를 눌렀을 때와 거점을 눌렀을 때 같은 자리에 뜬다 — 상태도 하나로 쓴다. */
  const [probeItems, setProbeItems] = useState([]);
  const [probeItemsLoading, setProbeItemsLoading] = useState(false);
  // 허브 거점은 품목이 많아 서버가 잘라 낸다. 잘렸다는 사실을 숨기지 않는다.
  const [probeTruncated, setProbeTruncated] = useState(false);
  const [flowItemCd, setFlowItemCd] = useState(null);
  const [flowSourcing, setFlowSourcing] = useState(null);
  /* 칩에 코드만 있으면 바로 위아래 줄의 비슷한 품목(MTB 엔트리 A / MTB 프로 A)과 헷갈린다 — 이름을 같이 단다. */
  const [flowItemName, setFlowItemName] = useState(null);
  /* 품목을 이미 아는 사람을 위한 입구 — 찾으면 곧장 「따라가기」다. 거르지 않는 건 똑같다.
     ItemAutocomplete 는 입력값을 ref 로 들고 있어 따라가기를 풀 때 여기서 비워 준다. */
  const followSearchRef = useRef("");
  // 강조 범위(홉). 1 은 예전 동작과 같다.
  const [depth, setDepth] = useState(reduxViewState?.depth ?? DEFAULT_DEPTH);

  const locationValueFormatter = params => {
    const { value } = params;

    return locationType?.[value] ? GridUtils.gridValueL10N(locationType[value]?.name) : value;
  }

  const dropDownValueFormatter = params => {
    return params.value ? params.value.name : "";
  }

  const dateFormatter = params => {
    return params.value ? DateUtils.formatDate(params.value, "yyyy-MM-dd") : "";
  }

  const locationFilterFormatter = params => {
    const filterVal = locations.filter(arr => arr.name === params.value);
    return filterVal.length > 0 ? filterVal[0].name : null;
  }

  const objectComparator = (value1, value2, colId) => {
    const property = isEqual(colId, "itemCd") ? "itemCd" : "name";
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    if (val1 === val2) {
      return 0;
    } else if (val1 > val2) {
      return 1;
    }
    return -1;
  }

  const dynamicEditable = params => {
    return isEqual(params.node.rowState, "created");
  }

  const siteValueFormatter = (site) => {
    return site.value ? site.value.name : "";
  };

  const locationColumnDefs = [
    { headerName: "locCd", field: "locCd", flex: 1, cellClass: 'stringType' },
    { headerName: "locName", field: "name", flex: 1 },
    {
      headerName: "locType", field: "locType", flex: 1,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: locationValueFormatter,
      cellEditorParams: {
        valueFormatter: locationValueFormatter,
        values: Object.keys(locationType)
      },
    },
    {
      headerName: "siteCd", field: "site", colId: "siteCd",
      type: ['nonEditableColumn'],
      valueFormatter: (site) => site.value ? site?.value?.siteCd : "",
      cellClass: 'stringType',
      comparator: (value1, value2) => objectComparator(value1, value2, "siteCd"),
      flex: 1
    },
    {
      headerName: "siteName", field: "site", colId: "siteName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter: siteValueFormatter,
      cellEditorParams: {
        values: sites
      },
      comparator: (value1, value2) => objectComparator(value1, value2, "name"),
      flex: 1
    },
    { headerName: "descTxt", field: "descTxt", flex: 1.2 },
    { headerName: "inhouseFlg", field: "inhouseFlg", type: ["booleanColumn"], width: 90 },
    { headerName: "activeFlg", field: "activeFlg", width: 90, type: ["booleanColumn"] }
  ];

  const bodColumnDefs = [
    {
      headerName: "originLocation", field: "originLocation", flex: 1,
      valueFormatter: dropDownValueFormatter,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      keyCreator: dropDownValueFormatter,
      cellEditorParams: {
        valueFormatter: dropDownValueFormatter,
        values: locations
      },
      filter: "agSetColumnFilter",
      filterParams: {
        valueFormatter: locationFilterFormatter
      },
      comparator: (value1, value2) => objectComparator(value1, value2, "name")
    },
    {
      headerName: "destLocation", field: "destLocation", colId: "destLocation", flex: 1,
      valueFormatter: dropDownValueFormatter,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      keyCreator: dropDownValueFormatter,
      cellEditorParams: {
        valueFormatter: dropDownValueFormatter,
        values: locations
      },
      filter: "agSetColumnFilter",
      filterParams: {
        valueFormatter: locationFilterFormatter
      },
      comparator: (value1, value2) => objectComparator(value1, value2, "name")
    },
    { headerName: "avgLtDays", field: "avgLtDays", width: 120, type: ["numberColumn", "rightAligned"], hide: true, suppressColumnsToolPanel: true },
    { headerName: "activeFlg", field: "activeFlg", width: 90, type: ["booleanColumn"] },
    { headerName: "updatedTs", field: "updatedTs", width: 140, type: ["nonEditableColumn"], valueFormatter: dateFormatter }
  ];

  const bodItemDefaultColumnDefs = [
    { headerName: "itemCd", field: "itemCd", flex: 1, cellClass: 'stringType' },
    { headerName: "itemName", field: "itemName", flex: 1.5, type: ["nonEditableColumn"] },
    { headerName: "activeFlg", field: "activeFlg", width: 80, type: ["booleanColumn"] },
    { headerName: "updatedTs", field: "updatedTs", width: 110, type: ["nonEditableColumn"], valueFormatter: dateFormatter }
  ];

  const suppliedItemsColumnDefs = [
    {
      headerName: "originLocation", field: "originLocation", width: 140,
      valueFormatter: dropDownValueFormatter, cellEditor: "agRichSelectCellEditor", type: ["nonEditableColumn", "defaultValueParser"],
      cellEditorPopup: true,
      keyCreator: dropDownValueFormatter,
      cellEditorParams: { valueFormatter: dropDownValueFormatter, values: originLocations },
      filter: "agSetColumnFilter", filterParams: { valueFormatter: locationFilterFormatter },
      editable: dynamicEditable,
      comparator: (value1, value2) => objectComparator(value1, value2, "name")
    },
    {
      headerName: "destLocation", field: "destLocation", colId: "destLocation", width: 140,
      valueFormatter: dropDownValueFormatter, type: ["nonEditableColumn"], keyCreator: dropDownValueFormatter,
      filter: "agSetColumnFilter", filterParams: { valueFormatter: locationFilterFormatter },
      comparator: (value1, value2) => objectComparator(value1, value2, "name")
    },
    ...bodItemDefaultColumnDefs
  ];

  const supplyingItemsColumnDefs = [
    {
      headerName: "originLocation", field: "originLocation", width: 140,
      valueFormatter: dropDownValueFormatter, type: ["nonEditableColumn"], keyCreator: dropDownValueFormatter,
      filter: "agSetColumnFilter", filterParams: { valueFormatter: locationFilterFormatter },
      comparator: (value1, value2) => objectComparator(value1, value2, "name")
    },
    {
      headerName: "destLocation", field: "destLocation", colId: "destLocation", width: 170,
      valueFormatter: dropDownValueFormatter, cellEditor: "agRichSelectCellEditor", type: ["nonEditableColumn", "defaultValueParser"],
      cellEditorPopup: true,
      keyCreator: dropDownValueFormatter,
      cellEditorParams: { valueFormatter: dropDownValueFormatter, values: destLocations },
      filter: "agSetColumnFilter", filterParams: { valueFormatter: locationFilterFormatter },
      editable: dynamicEditable,
      comparator: (value1, value2) => objectComparator(value1, value2, "name")
    },
    ...bodItemDefaultColumnDefs
  ];

  const defaultLocationGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, columnDefs: locationColumnDefs, gridId: LOCATION_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultBodGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, columnDefs: bodColumnDefs, gridId: BOD_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultSuppliedBodItemGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, columnDefs: suppliedItemsColumnDefs, gridId: SUPPLIED_BOD_ITEM_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultSupplyingBodItemGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, columnDefs: supplyingItemsColumnDefs, gridId: SUPPLYING_BOD_ITEM_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
    그래프 모델. 화면 크기와 무관한 순수 계산이라 데이터가 바뀔 때만 다시 만든다.
    레이아웃(픽셀 좌표)까지 여기서 미리 확정해 두면, 확대/축소는 CSS transform 만 건드리면 된다.

    품목을 짚어도 이 모델은 바뀌지 않는다 — 거르지 않고 칠하기만 하기 때문이다.
    그래서 품목을 바꿔 짚어도 계층 계산과 레이아웃이 다시 돌지 않는다.
  */
  const network = useMemo(
    () => buildNetwork({ locations, bods: activeBods, itemCounts: bodItemCounts }),
    [locations, activeBods, bodItemCounts]
  );
  const graphLayout = useMemo(() => layoutGraph(network), [network]);
  const flowKeys = useMemo(() => linksForBods(network, flowSourcing), [network, flowSourcing]);

  const setSelectLocation = gridApi => {
    if (!isEmpty(locations)) {
      if (!isNull(selectedLocation)) {
        gridApi.forEachNode(node => {
          if (isEqual(selectedLocation.locCd, node.data.locCd)) {
            node.setSelected(true);
          }
        });
        GridUtils.focusOnSelectedRow(gridApi);
      }
    }
  }

  const retrieveDistributionNetwork = () => {
    rpDistributionNetworkService.getDistributionNetwork()
      .then(response => {
        const { results, locationResults, deleteTableLoc, deleteTableBod, bodItemCounts: itemCounts } = response.data;
        for (const loc of locationResults) {
          loc.site = siteMap[loc.siteCd]
        }
        setLocations(locationResults);
        setBillOfDistribution(results);
        setBodItemCounts(itemCounts ?? {});
        if (!isEmpty(deleteTableLoc)) {
          setDeleteTableLocation(deleteTableLoc);
        }
        if (!isEmpty(deleteTableBod)) {
          setDeleteTableBod(deleteTableBod)
        }

        const filteredBods = results.filter(result => isEqual(result.activeFlg, true));
        setActiveBods(filteredBods);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        DataState.initialize(nodeGridRef.current?.api);
        DataState.initialize(bodGridRef.current?.api);
        if (nodeGridRef.current?.api) {
          setSelectLocation(nodeGridRef.current.api);
        }
        /*
          BOD 가 바뀌었을 수 있으므로 짚어 둔 것들을 정리한다.
          링크·품목 선택은 서버 데이터에 매달린 상태라 redux 에 담지 않는다 — 담아 두면
          Table 뷰에서 링크를 지우고 돌아왔을 때 없는 링크를 설명하게 된다.
        */
        clearProbe();
      });
  }

  const clearProbe = () => {
    setSelectedLink(null);
    setSelectedBundle(null);
    setProbeItems([]);
    setFlowItemCd(null);
    setFlowItemName(null);
    setFlowSourcing(null);
  }

  /*
    합쳐진 줄기를 눌렀을 때.
    갈래가 하나뿐인 줄기가 대부분이라(번들 판정은 lane 전체 부채꼴로 하고 묶음은 밴드별로 나뉜다)
    그 경우는 고르는 단계 없이 바로 링크로 보낸다 — 고를 게 하나뿐인 목록을 보여 줄 이유가 없다.
  */
  const selectTrunk = trunk => {
    const links = trunk?.links ?? [];
    if (links.length === 1) {
      selectLink(links[0]);
      return;
    }
    if (isEqual(selectedBundle?.key, trunk.key)) {
      setSelectedBundle(null);
      return;
    }
    selectLocation(null);
    setSelectedLink(null);
    setProbeItems([]);
    setSelectedBundle(trunk);
  }

  /*
    링크 하나를 타는 품목. 선을 누를 때만 도는 조회다 —
    링크마다 품목 목록을 미리 실어 내리면 응답이 z_bod_item 전체 크기가 된다.
  */
  const selectLink = link => {
    if (!link || isEqual(selectedLink?.key, link.key)) {
      setSelectedLink(null);
      setProbeItems([]);
      return;
    }

    // 링크를 보는 동안은 거점·줄기 선택을 비운다 — 인스펙터가 한 번에 하나만 설명한다.
    selectLocation(null);
    setSelectedBundle(null);
    setSelectedLink(link);
    setProbeItems([]);
    setProbeItemsLoading(true);

    rpDistributionNetworkService.getLinkItems(link.id)
      .then(response => {
        setProbeItems(response?.data?.results ?? []);
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_RetrieveFailed") });
      })
      .finally(() => {
        setProbeItemsLoading(false);
      });
  }

  /*
    거점 하나를 드나드는 품목. 거점을 누를 때만 도는 조회다.
    링크와 같은 자리·같은 모양으로 뜨고, 여기서도 바로 「따라가기」로 넘어갈 수 있다 —
    "어느 링크인지"를 먼저 정하지 않아도 품목을 짚을 수 있어야 한다.
  */
  const retrieveLocationItems = locCd => {
    setProbeItemsLoading(true);

    rpDistributionNetworkService.getLocationItems(locCd)
      .then(response => {
        setProbeItems(response?.data?.results ?? []);
        setProbeTruncated(Boolean(response?.data?.truncated));
      })
      .catch(e => {
        debug(e);
        setProbeItems([]);
        setProbeTruncated(false);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_RetrieveFailed") });
      })
      .finally(() => {
        setProbeItemsLoading(false);
      });
  }

  /*
    품목 하나를 짚어 그 경로를 그래프에 칠한다. 거르지 않으므로 전체 그림은 그대로다.
    응답이 BOD 마다 세 값(id·우선순위·비율)뿐이라 품목을 바꿔 짚어도 왕복 비용이 거의 없다.
  */
  const followItem = (itemCd, knownName = null) => {
    if (isEmpty(itemCd)) {
      followSearchRef.current = "";
      setFlowItemCd(null);
      setFlowItemName(null);
      setFlowSourcing(null);
      return;
    }

    rpDistributionNetworkService.getItemBods(itemCd)
      .then(response => {
        // 구 응답(bodIds 배열)도 받는다 — 백엔드가 먼저 안 올라간 환경에서도 따라가기는 되고 라벨만 빠진다.
        const bods = response?.data?.bods
          ?? (response?.data?.bodIds ?? []).map(bodId => ({ bodId }));
        if (isEmpty(bods)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_NoDistributionForItem") });
          return;
        }
        // 인스펙터에서 짚었든 검색으로 짚었든 검색칸이 지금 따라가는 품목을 보여 준다.
        followSearchRef.current = itemCd;
        setFlowItemCd(itemCd);
        // 인스펙터에서 짚으면 이름을 이미 안다. 검색으로 짚으면 코드뿐이라 응답에 실려 온 이름을 쓴다.
        setFlowItemName(knownName || bods.find(b => b.itemName)?.itemName || null);
        // bodId 는 16자리를 넘으면 axios-bigint 가 BigInt 로 파싱한다 — Number() 로 바꾸면 link.id(BigInt)와 키가 안 맞는다.
        setFlowSourcing(new Map(bods.map(b => [b.bodId, b])));
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_RetrieveFailed") });
      });
  }

  const handleDepthChange = nextDepth => {
    setDepth(nextDepth);
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, { depth: nextDepth })));
  }

  const retrieveSites = () => {
    siteService.getAllForCSV({})
      .then(response => {
        const { results } = response.data;
        if (!isEmpty(results)) {
          setSites([{ name: t("none"), siteCd: "" }, ...results]);
          const map = {};
          for (const result of results) {
            map[result.siteCd] = result;
          }
          setSiteMap(map)
        }
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        setLoading(true)
      });
  }

  const setDropdownBodItemLocations = locationCode => {
    const originLocs = [];
    const destLocs = [];

    for (const bod of billOfDistribution) {
      const { originLocation, destLocation } = bod;
      if (isEqual(locationCode, destLocation.locCd)) {
        originLocs.push(originLocation);
      }
      if (isEqual(locationCode, originLocation.locCd)) {
        destLocs.push(destLocation);
      }
    }

    setOriginLocations(originLocs);
    setDestLocations(destLocs);
  }

  /*
   * 그리드가 아직 준비되지 않았을 수 있다 — api 까지 옵셔널로 받는다.
   *   Table 뷰의 그리드들은 그래프 뷰에서는 아예 마운트되지 않는다. 그래서 그래프에서 위치를 고른 뒤
   *   Table 로 넘어오면, 뷰 전환으로 이 조회가 다시 도는 시점에 ref.current 는 생겼지만
   *   api 는 onGridReady 전이라 undefined 다. `current?.api?.setGridOption(...)` 는 current 만
   *   막아 주므로 여기서 터졌고, 화면 전체가 하얗게 됐다.
   *   (로딩 표시만 건너뛸 뿐 데이터는 rowData 로 들어가므로 동작에는 영향이 없다.)
   */
  const retrieveSuppliedItems = locationCode => {
    suppliedProdGridRef.current?.api?.setGridOption('loading', true);

    BodItemService.getAll({ pagination: false, searchFilters: { destLocCd: locationCode } })
      .then(response => {
        const { results } = response.data;
        setSuppliedItems(results);
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        suppliedProdGridRef.current?.api?.setGridOption('loading', false);
        DataState.initialize(suppliedProdGridRef.current?.api);
      });
  }

  const retrieveSupplyingItems = locationCode => {
    supplyingProdGridRef.current?.api?.setGridOption('loading', true);

    BodItemService.getAll({ pagination: false, searchFilters: { originLocCd: locationCode } })
      .then(response => {
        const { results } = response.data;
        setSupplyingItems(results);
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        supplyingProdGridRef.current?.api?.setGridOption('loading', false);
        DataState.initialize(supplyingProdGridRef.current?.api);
      });
  }

  const addRow = (gridApi, data = {}) => {
    if (gridApi) {
      const newRow = { id: null, activeFlg: true, ...data };
      const createdNode = GridUtils.addRow(gridApi, newRow);

      DataState.setDataState(createdNode);
    }
  }

  const saveLocation = () => {
    nodeGridRef.current.api.stopEditing(false);
    const allStateData = DataState.getAllStateData(nodeGridRef.current.api);

    if (allStateData.length > 0) {
      locationService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
        .then(() => {
          retrieveDistributionNetwork();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveBod = () => {
    const allStateData = DataState.getAllStateData(bodGridRef.current.api);
    if (allStateData.length > 0) {
      for (const data of allStateData) {
        const { destLocation, originLocation } = data;
        if (isEqual(destLocation.id, originLocation.id)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_OriginDestNotSame") });
          return;
        }
      }

      BodService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
        .finally(() => {
          retrieveDistributionNetwork();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveBodItem = (target, gridApi) => {
    gridApi.stopEditing(false);

    const allStateData = DataState.getAllStateData(gridApi);

    if (allStateData.length > 0) {
      BodItemService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
        .finally(() => {
          retrieveDistributionNetwork();
          if (!isNull(selectedLocation)) {
            if (isEqual(target, "SUPPLIED_BOD_ITEM")) {
              retrieveSuppliedItems(selectedLocation.locCd);
            } else {
              retrieveSupplyingItems(selectedLocation.locCd);
            }
          }
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const resetBodItem = () => {
    setSuppliedItems([]);
    setSupplyingItems([]);
  }

  const deleteLocation = () => {
    const locationIds = [];
    nodeGridRef.current.api.stopEditing(false);
    const deleteNodes = nodeGridRef.current.api.getSelectedNodes();
    for (const node of deleteNodes) {
      if (node?.data?.id && !isNull(node.data.id)) {
        locationIds.push(node.data.id);
      }
    }

    if (!isEmpty(locationIds)) {
      locationService.remove(locationIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveDistributionNetwork();
          resetBodItem();
        });
    }

    if (deleteNodes.length > 0) {
      DataState.deleteState(nodeGridRef.current.api, deleteNodes);
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedData") });
    }
  }

  const deleteBod = () => {
    const bodIds = [];
    const deleteNodes = bodGridRef.current.api.getSelectedNodes();
    for (const node of deleteNodes) {
      if (node?.data?.id && !isNull(node.data.id)) {
        bodIds.push(node.data.id);
      }
    }

    if (!isEmpty(bodIds)) {
      BodService.remove(bodIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .finally(() => {
          retrieveDistributionNetwork();
        });
    }

    if (deleteNodes.length > 0) {
      DataState.deleteState(bodGridRef.current.api, deleteNodes);
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedData") });
    }
  }

  const deleteBodItem = (target, gridApi) => {
    gridApi.stopEditing(false);

    const bodIds = [];
    const deleteNodes = gridApi.getSelectedNodes();
    for (const node of deleteNodes) {
      if (node?.data?.id && !isNull(node.data.id)) {
        bodIds.push(node.data.id);
      }
    }

    if (!isEmpty(bodIds)) {
      BodItemService.remove(bodIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .finally(() => {
          retrieveDistributionNetwork();
          if (!isNull(selectedLocation)) {
            if (isEqual(target, "SUPPLIED_BOD_ITEM")) {
              retrieveSuppliedItems(selectedLocation.locCd);
            } else {
              retrieveSupplyingItems(selectedLocation.locCd);
            }
          }
        });
    }

    if (deleteNodes.length > 0) {
      DataState.deleteState(gridApi, deleteNodes);
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedData") });
    }
  }

  // 선택은 네 뷰의 공통 상태다. 그래프·목록·그리드 어디서 골라도 여기로 모인다.
  const selectLocation = data => {
    setSelectedLocation(data ?? null);
    /* 거점을 고르면 링크·줄기 설명을 접는다 — 인스펙터는 한 번에 하나만 설명한다.
       따라가는 품목은 그대로 둔다: 경로를 켜 둔 채 거점을 짚어 보는 게 이 화면의 주 동선이다. */
    setSelectedLink(null);
    setSelectedBundle(null);
    setProbeItems([]);

    if (data?.locCd) {
      setDropdownBodItemLocations(data.locCd);
      reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, { selectedLocation: data })));
    } else {
      reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, { selectedLocation: null })));
    }
  }

  /*
   * 그래프·목록의 공통 선택 경로.
   *   같은 위치를 다시 누르면 선택을 푼다 — 예전에는 해제하는 길이 아예 없어서, 한 번 고르면
   *   다른 위치를 고르는 것 말고는 전체 보기로 돌아올 수 없었다(선택은 필터가 아니라 강조라
   *   나머지가 흐려진 채로 남는다). 빈 캔버스 클릭도 같은 일을 한다(NetworkCanvas 참고).
   */
  const selectLocationByCode = locCd => {
    if (locCd && isEqual(selectedLocation?.locCd, locCd)) {
      selectLocation(null);
      return;
    }
    selectLocation(locations.find(loc => isEqual(loc.locCd, locCd)) ?? null);
  }

  const onRowSelected = params => {
    const { node, data } = params;

    if (node.isSelected() && data?.locCd) {
      selectLocation(data);
      return;
    }

    const selectedRows = params.api.getSelectedRows();
    if (selectedRows.length === 0 || !data?.locCd) {
      selectLocation(null);
    }
  }

  const initCheck = params => {
    if (params.type === "componentStateChanged") {
      setSelectLocation(params.api);
    }
  }

  const handleDialog = (action, _falseAction, el) => {
    if (el?.target && el?.gridApi) {
      const { target, gridApi } = el;

      if (action === "SAVE") {
        if (isEqual(target, "LOCATION")) {
          saveLocation();
        } else if (isEqual(target, "BOD")) {
          saveBod();
        } else if (isEqual(target, "SUPPLIED_BOD_ITEM") || isEqual(target, "SUPPLYING_BOD_ITEM")) {
          saveBodItem(target, gridApi);
        }
      } else if (action === "DELETE") {
        if (isEqual(target, "LOCATION")) {
          deleteLocation();
        } else if (isEqual(target, "BOD")) {
          deleteBod();
        } else if (isEqual(target, "SUPPLIED_BOD_ITEM") || isEqual(target, "SUPPLYING_BOD_ITEM")) {
          deleteBodItem(target, gridApi);
        }
      }
    }
  }

  const handleViewChange = nextType => {
    setType(nextType);
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, { type: nextType })));
  }

  // 그래프 노드 우클릭 → RP Policy / RP Demand 로 이 위치를 검색 조건으로 열기(기존 동작 유지).
  const handleNodeContextMenu = (e, node) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, kind: DIST_KIND.LOCATION, code: node.locCd, name: node.name, nodeType: node.locType });
  }

  const handleNavigate = action => {
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(action.reduxKey, action.payload(ctxMenu))));
    reduxDispatch(reduxUtil.updateHistoryState(action.reduxKey));
    setCtxMenu(null);
  }

  useEffect(() => {
    if (loading) {
      retrieveDistributionNetwork();
    }

    return () => {
      setLocations([]);
      setBillOfDistribution([]);
      setSuppliedItems([]);
      setSupplyingItems([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    retrieveSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
    Graph 뷰에서 거점을 고르면 그 거점을 드나드는 품목을 받는다.
    링크가 선택돼 있으면 그쪽이 우선이라 건너뛴다 — 인스펙터는 한 번에 하나만 설명한다.
  */
  useEffect(() => {
    if (!isEqual(type, VIEW.GRAPH) || selectedLink || selectedBundle) {
      return;
    }
    if (selectedLocation?.locCd) {
      retrieveLocationItems(selectedLocation.locCd);
    } else {
      setProbeItems([]);
      setProbeTruncated(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, selectedLocation, selectedLink, selectedBundle]);

  // 품목 그리드는 Table 뷰에서만 보인다 — 그래프에서 노드를 훑을 때마다 조회하지 않는다.
  useEffect(() => {
    if (!isEqual(type, VIEW.TABLE)) {
      return;
    }
    if (selectedLocation?.locCd) {
      retrieveSuppliedItems(selectedLocation.locCd);
      retrieveSupplyingItems(selectedLocation.locCd);
    } else {
      resetBodItem();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, selectedLocation]);

  useEffect(() => {
    if (!isEmpty(locations) && !isEmpty(sites)) {
      GridUtils.setColumnDefs({ ...bodGridRef.current, columnDefs: bodColumnDefs });
      GridUtils.setColumnDefs({ ...nodeGridRef.current, columnDefs: locationColumnDefs, viewName, gridId: LOCATION_GRID });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, sites]);

  useEffect(() => {
    if (!isEmpty(originLocations)) {
      GridUtils.setColumnDefs({ ...suppliedProdGridRef.current, columnDefs: suppliedItemsColumnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originLocations]);

  useEffect(() => {
    if (!isEmpty(destLocations)) {
      GridUtils.setColumnDefs({ ...supplyingProdGridRef.current, columnDefs: supplyingItemsColumnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destLocations]);

  const makeMessage = (deleteTable) => {
    let message = "";
    if (deleteTable.length > 0) {
      for (let i = 0; i < deleteTable.length; i++) {
        const data = deleteTable[i];
        if (!CodeUtil.tableToMenuName[data]) {
          continue;
        }
        const name = t(CodeUtil.tableToMenuName[data]);
        message += `- ${name}\n`;
      }
    }
    return message;
  }

  const setDisabled = nodeType => {
    if (selectedLocation?.nodeType) {
      return isEqual(selectedLocation.nodeType, nodeType);
    }
    return true;
  }

  const BaseButton = ({ target, gridApi, disabled, addedData = {} }) => {
    const DeleteContent = contentTarget => {
      let content = t("MSG_DeleteConfirm");
      switch (contentTarget) {
        case "LOCATION":
          content = `${t("MSG_DeleteDataAssociated")}\n\n${makeMessage(deleteTableLocation)}`;
          break;
        case "BOD":
          content = `${t("MSG_DeleteDataAssociated")}\n\n${makeMessage(deleteTableBod)}`;
          break;

        default:
          break;
      }

      return content;
    }

    return (
      <Box sx={{ ml: "auto" }}>
        <AddButton disabled={disabled} onClick={() => addRow(gridApi, addedData)} />
        <RemoveButton disabled={disabled} onClick={() => {
          setDialogInfo({ open: true, title: "delete", content: DeleteContent(target), el: { target, gridApi } })
        }} />
        <SaveButton disabled={disabled} onClick={() => {
          setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm"), el: { target, gridApi } })
        }} />
      </Box>
    );
  }

  const gridStyle = { display: "flex", flexDirection: "column", height: "50%" };
  // 권한 밖 화면은 우클릭 메뉴에서 뺀다 — 사이드바에 없는 화면이 링크로만 남으면 안 된다.
  const navActions = ctxMenu ? getNodeNavActions(ctxMenu).filter((a) => isPermitted(a.reduxKey)) : [];

  /*
    강조 범위. 캔버스에 대해 작용하므로 페이지 헤더가 아니라 캔버스 머리줄에 둔다.
    「전체」는 허브를 고르면 네트워크 대부분이 켜져 강조가 의미를 잃으므로 기본값이 아니다.
  */
  /*
    범위는 "고른 거점에서 몇 단계"라 기준 거점이 있어야 뜻이 있다. 품목만 따라가는 중이면 칠할 곳이
    경로 전체로 정해져 있어 눌러도 아무 일도 안 일어난다 — 그때는 잠그고 거점을 고르라고 알려 준다.
  */
  const depthIdle = Boolean(flowItemCd) && !selectedLocation?.locCd;
  const depthControl = (
    <Tooltip title={depthIdle ? t("MSG_SelectLocationToInspect") : ""}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography sx={{ fontSize: FS.CAPTION, color: depthIdle ? "text.disabled" : "text.secondary", whiteSpace: "nowrap" }}>
          {t("highlightRange")}
        </Typography>
        <SegmentedControl
          value={depth}
          onChange={handleDepthChange}
          options={[
            { value: 1, label: "1" },
            { value: 2, label: "2" },
            { value: 3, label: "3" },
            { value: DEPTH_ALL, label: t("all") },
          ].map(opt => ({ ...opt, disabled: depthIdle }))}
        />
      </Box>
    </Tooltip>
  );

  const graphControls = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      {/* 코드를 끝까지 치고 Enter — 입력값이 Autocomplete 의 value 와 같아 onChange 가 안 온다
          (setSelectedItem 모드는 Enter 에 retrieve 도 부르지 않는다). 공용 컴포넌트는 두고 여기서 받는다. */}
      <Box
        onKeyUp={e => {
          const code = resolveItemCd(followSearchRef.current);
          if (e.key === "Enter" && code && code !== flowItemCd) {
            followItem(code);
          }
        }}
      >
        <RPItemAutocomplete
          searchValue={followSearchRef}
          searchFields={FOLLOW_SEARCH_FIELDS}
          setSelectedItem={selected => followItem(resolveItemCd(selected))}
          sx={{ width: 200, minWidth: 200, mr: 0 }}
        />
      </Box>
      {depthControl}
    </Box>
  );

  /*
    따라가는 품목을 캔버스 아래에 계속 띄워 둔다.
    경로를 칠한 채 스크롤하거나 확대하다 보면 "왜 이 선만 진하지?"를 잊는다 — 여기서 이름과
    해제 버튼을 같이 들고 있는다. 짚은 게 없으면 자리도 차지하지 않는다.
  */
  /*
    거점을 고르면 선이 두 톤(받아 오는 쪽 / 보내는 쪽)으로 갈린다 — 어느 톤이 어느 쪽인지 여기서 알려 준다.
    톤은 그래프와 같은 함수에서 뽑는다(따라가는 중이면 품목색, 아니면 선택색 기준).
  */
  const reachLegendTones = reachTones(flowItemCd ? ColorUtil.distNetworkFlowColor() : theme.palette.primary.main);
  const reachLegend = selectedLocation?.locCd ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, ml: "auto" }}>
      {[
        { dir: REACH_DIR.UP, label: t("inbound") },
        { dir: REACH_DIR.DOWN, label: t("outbound") },
      ].map(({ dir, label }) => (
        <Box key={dir} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 14, height: 3, borderRadius: 999, bgcolor: reachLegendTones[dir] }} />
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>
            {`${REACH_ARROW[dir]} ${label}`}
          </Typography>
        </Box>
      ))}
    </Box>
  ) : null;

  const graphFooter = flowItemCd || reachLegend ? (
    <Box sx={{
      flexShrink: 0, px: 1.5, py: 0.75, borderTop: 1, borderColor: "divider",
      display: "flex", alignItems: "center", gap: 1,
    }}>
      {flowItemCd && (
        <>
          <Chip
            size="small"
            label={[t("following"), flowItemCd, flowItemName].filter(Boolean).join(" · ")}
            onDelete={() => followItem(null)}
            sx={{
              height: 22, fontSize: FS.CAPTION,
              ...ColorUtil.tintChipSx(ColorUtil.distNetworkFlowColor()),
              fontWeight: 500,
            }}
          />
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>
            {`${t("links")} ${flowKeys.size}`}
          </Typography>
        </>
      )}
      {reachLegend}
    </Box>
  ) : null;

  return (
    <>
      {/* 헤더 — 이 네트워크가 얼마나 큰지(위치·링크·품목)와 어떤 눈으로 볼지(뷰)를 한 줄에 둔다.
          품목은 여기에 두지 않는다: 거르지 않고 짚어 들어가므로 페이지 머리줄에 걸 상태가 없다 —
          품목 검색은 그래프 머리줄에서 「따라가기」의 입구로만 쓴다. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", height: 50, width: "100%" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t("distributionNetwork")}</Typography>
        <StatPill label={t("location")} value={network.locCount} />
        <StatPill label={t("links")} value={network.linkCount} />
        <StatPill label={t("items")} value={network.itemTotal} />
        <Box sx={{ flex: 1 }} />
        <SegmentedControl
          value={type}
          onChange={handleViewChange}
          options={[
            { value: VIEW.GRAPH, label: t("graph") },
            { value: VIEW.TABLE, label: t("table") },
          ]}
        />
      </Box>

      {isEqual(type, VIEW.TABLE) ? (
        <Box className={"grid-area"} sx={{ height: "calc(100% - 50px)" }}>
          <Grid container spacing={1.5} sx={{ width: "calc(100% + 10px)", height: "calc(100% + 6px)" }}>
            <Grid item sm={6} xs={12} sx={gridStyle}>
              <Box sx={{ display: "flex" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, display: "flex", alignItems: "center" }}>
                  {t("location")}
                </Typography>
                <BaseButton target="LOCATION" gridApi={nodeGridRef.current?.api} disabled={false}></BaseButton>
              </Box>
              <Box id="location-ag-grid" className="ag-theme-balham" sx={{ mt: 1, height: "100%" }}>
                <AgGridReact
                  ref={nodeGridRef}
                  rowData={locations}
                  {...defaultLocationGridMemo}
                  rowSelection={{ mode: 'singleRow', enableClickSelection: true }}
                  onGridReady={params => GridUtils.setColumnDefs({ ...params, columnDefs: locationColumnDefs, viewName, gridId: LOCATION_GRID, initState: true })}
                  onRowSelected={onRowSelected}
                  onComponentStateChanged={initCheck}
                />
              </Box>
            </Grid>
            <Grid item sm={6} xs={12} sx={gridStyle}>
              <Box sx={{ display: "flex" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, display: "flex", alignItems: "center", mr: 2 }}>{t("distributionNetwork")}</Typography>
                {bodGridApi ? <BaseButton target="BOD" gridApi={bodGridApi} disabled={false} /> : null}
              </Box>
              <Box id="distribution-ag-grid" className="ag-theme-balham" sx={{ mt: 1, height: "100%" }}>
                <AgGridReact
                  ref={bodGridRef}
                  rowData={billOfDistribution}
                  {...defaultBodGridMemo}
                  onGridReady={params => {
                    setBodGridApi(params.api);
                    GridUtils.setColumnDefs({ ...params, columnDefs: bodColumnDefs, viewName, gridId: BOD_GRID, initState: true });
                  }}
                  rowSelection={{ mode: 'singleRow', enableClickSelection: true }}
                />
              </Box>
            </Grid>
            <Grid item sm={6} xs={12} sx={gridStyle}>
              <Box sx={{ display: "flex" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {t("suppliedItems")}
                </Typography>
                <BaseButton target="SUPPLIED_BOD_ITEM" gridApi={suppliedProdGridRef.current?.api} disabled={setDisabled("root")} addedData={{ destLocation: selectedLocation }}></BaseButton>
              </Box>
              <Box id="supplied-ag-grid" className="ag-theme-balham" sx={{ mt: 1, height: "100%" }}>
                <AgGridReact
                  ref={suppliedProdGridRef}
                  rowData={suppliedItems}
                  {...defaultSuppliedBodItemGridMemo}
                  onGridReady={params => GridUtils.setColumnDefs({ ...params, columnDefs: suppliedItemsColumnDefs, viewName, gridId: SUPPLIED_BOD_ITEM_GRID, initState: true })}
                />
              </Box>
            </Grid>
            <Grid item sm={6} xs={12} sx={gridStyle}>
              <Box sx={{ display: "flex" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, display: "flex", alignItems: "center" }}>
                  {t("supplyingItems")}
                </Typography>
                <BaseButton target="SUPPLYING_BOD_ITEM" gridApi={supplyingProdGridRef.current?.api} disabled={setDisabled("leaf")} addedData={{ originLocation: selectedLocation }}></BaseButton>
              </Box>
              <Box id="supplying-ag-grid" className="ag-theme-balham" sx={{ mt: 1, height: "100%" }}>
                <AgGridReact
                  ref={supplyingProdGridRef}
                  rowData={supplyingItems}
                  {...defaultSupplyingBodItemGridMemo}
                  onGridReady={params => GridUtils.setColumnDefs({ ...params, columnDefs: supplyingItemsColumnDefs, viewName, gridId: SUPPLYING_BOD_ITEM_GRID, initState: true })}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        /*
          목록 | 캔버스 | 인스펙터. 가운데만 늘어나고 양옆은 고정폭이다 —
          좁은 화면에서는 세 칸을 접는 대신 가로 스크롤(.grid-area 가 overflow:auto)로 둔다.
          접어 버리면 "찾고 → 보고 → 확인한다"는 이 화면의 동선 자체가 끊긴다.
        */
        <Box className={"grid-area"} sx={{ height: "calc(100% - 50px)", width: "100%" }}>
          <AppCard sx={{ height: "100%", minWidth: 1080, display: "flex" }}>
            <Box sx={{ width: 280, flexShrink: 0, borderRight: 1, borderColor: "divider", minHeight: 0 }}>
              <DistributionNetworkLocationList
                network={network}
                selectedCd={selectedLocation?.locCd ?? null}
                onSelect={selectLocationByCode}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
              <DistributionNetworkGraph
                network={network}
                layout={graphLayout}
                selectedCd={selectedLocation?.locCd ?? null}
                depth={depth}
                selectedLinkKey={selectedLink?.key ?? null}
                selectedBundleKey={selectedBundle?.key ?? null}
                flowKeys={flowKeys}
                flowSourcing={flowSourcing}
                controls={graphControls}
                footer={graphFooter}
                onSelect={selectLocationByCode}
                onSelectLink={selectLink}
                onSelectTrunk={selectTrunk}
                onNodeContextMenu={handleNodeContextMenu}
              />
            </Box>
            <Box sx={{ width: 306, flexShrink: 0, borderLeft: 1, borderColor: "divider", minHeight: 0 }}>
              <DistributionNetworkInspector
                network={network}
                selectedCd={selectedLocation?.locCd ?? null}
                depth={depth}
                selectedLink={selectedLink}
                selectedBundle={selectedBundle}
                items={probeItems}
                itemsLoading={probeItemsLoading}
                itemsTruncated={probeTruncated}
                flowItemCd={flowItemCd}
                flowKeys={flowKeys}
                flowSourcing={flowSourcing}
                onFollowItem={followItem}
                onSelect={selectLocationByCode}
                onSelectLink={selectLink}
              />
            </Box>
          </AppCard>
        </Box>
      )}

      {/* 그래프 노드 우클릭 메뉴 — 관련 화면으로 이동.
          disableScrollLock 필수: 없으면 메뉴 열 때 body padding 이 변해 캔버스의 ResizeObserver 가
          발화하고 fit 이 다시 호출되어 그림이 튄다. */}
      <Menu
        // 권한 필터로 이동 액션이 전부 빠지면 노드 코드만 남은 빈 메뉴가 되므로 아예 열지 않는다.
        open={!!ctxMenu && navActions.length > 0}
        onClose={() => setCtxMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}
        disableScrollLock
        PaperProps={{ sx: { borderRadius: 1.5, minWidth: 210, boxShadow: 4, mt: 0.5 } }}
        MenuListProps={{ dense: true, sx: { py: 0.5 } }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 2, pt: 0.5, pb: 0.75 }}>
          {ctxMenu?.code}
        </Typography>
        {navActions.map((action) => (
          <MenuItem key={action.id} onClick={() => handleNavigate(action)} sx={{ gap: 1.25, py: 0.75, px: 1.5, mx: 0.5, borderRadius: 1 }}>
            <OpenInNewIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography variant="body2">{t(action.titleKey)}</Typography>
          </MenuItem>
        ))}
      </Menu>

      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
        el={dialogInfo.el}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  )
};

export default withTranslation()(RpDistributionNetwork);
